import { datadogRum } from '@datadog/browser-rum'
import type { Router } from 'vue-router'
import debug from '../utils/console'
import { isDebugEnabled } from '../utils/debug/isDebugEnabled'
import {
  createAdRequestLifecycleTracker,
  observeAdVisible,
  setAdRequestLifecycleTracker,
} from '../utils/ad-ops/adRequestLifecycle'
import type { LifecycleSlot } from '../utils/ad-ops/adRequestLifecycle'
import {
  consentSnapshotContext,
  firstAdRequestSnapshot,
} from '../utils/consent/firstAdRequest'

const logger = debug.withTag('ad-request-lifecycle')

interface SlotEvent {
  slot: LifecycleSlot
}
interface SlotRenderEndedEvent extends SlotEvent {
  isEmpty: boolean
}

const isPrerendering = () => {
  const doc: Document & { prerendering?: boolean } = document
  return doc.prerendering === true
}

// Zero until a prerendered page is shown.
const activationStart = () => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const navigation = performance.getEntriesByType('navigation')[0] as
    | (PerformanceNavigationTiming & { activationStart?: number })
    | undefined
  return navigation?.activationStart ?? 0
}

// The consent decision as it stood at the page load's first ad request, the
// same tags as first_ad_visible.
function consentContext(): Record<string, string> {
  try {
    const snapshot = firstAdRequestSnapshot()
    return {
      consent_decision: snapshot?.state ?? 'none',
      first_ad_request: snapshot?.provider ?? 'none',
      ...consentSnapshotContext(snapshot),
    }
  } catch {
    return {}
  }
}

const withoutTrailingSlash = (path: string) => path.replace(/\/+$/, '') || '/'

// GPT runs its listeners synchronously, next to the ones that render the
// fallback, so each one copies what it needs and swallows its own failure.
const guarded =
  <E>(handler: (event: E) => void) =>
  (event: E) => {
    try {
      handler(event)
    } catch {
      // A measurement only: lose it, keep the ad.
    }
  }

/**
 * Sends each ad request's lifecycle and outcome to RUM. See
 * utils/ad-ops/adRequestLifecycle.ts for the vitals and their tags.
 *
 * Its own GPT listeners, separate from first-ad-visible, which removes its
 * listeners once the first ad showed. Measuring stops when the visitor leaves
 * the landing path or the page is hidden.
 */
function startTracking(router: Router) {
  const debugEnabled = isDebugEnabled()
  const cleanups: Array<() => void> = []
  const tracker = createAdRequestLifecycleTracker({
    now: () => performance.now(),
    timeOrigin: performance.timeOrigin,
    pageStart: activationStart,
    send: ({ name, startTime, duration, context }) => {
      if (debugEnabled) logger.info('Ad request', name, duration, context)
      datadogRum.addDurationVital(name, { startTime, duration, context })
    },
    context: consentContext,
    observeVisible: observeAdVisible,
    onStop: () => {
      setAdRequestLifecycleTracker(undefined)
      cleanups.forEach((cleanup) => cleanup())
    },
  })
  setAdRequestLifecycleTracker(tracker)

  // Listened for on the window in the capture phase, where the RUM SDK
  // flushes its exit beacon on the same event. Added before the SDK starts
  // (app.vue, after the plugins), it runs first, so the summary makes that
  // flush, including when the page is being unloaded.
  const stopWhenHidden = () => {
    if (document.visibilityState === 'hidden' && !isPrerendering()) {
      tracker.stop()
    }
  }
  window.addEventListener('visibilitychange', stopWhenHidden, {
    capture: true,
  })
  cleanups.push(() =>
    window.removeEventListener('visibilitychange', stopWhenHidden, {
      capture: true,
    })
  )

  // A query change (a category tab) is the same page. Another path is not.
  cleanups.push(
    router.afterEach((to, from) => {
      if (
        from.matched.length > 0 &&
        withoutTrailingSlash(to.path) !== withoutTrailingSlash(from.path)
      ) {
        tracker.stop()
      }
    })
  )

  const listeners: Record<string, (event: never) => void> = {
    slotRequested: guarded(({ slot }: SlotEvent) =>
      tracker.slotRequested(slot)
    ),
    slotResponseReceived: guarded(({ slot }: SlotEvent) =>
      tracker.slotResponseReceived(slot)
    ),
    slotRenderEnded: guarded(({ slot, isEmpty }: SlotRenderEndedEvent) =>
      tracker.slotRenderEnded(slot, isEmpty === true)
    ),
    slotOnload: guarded(({ slot }: SlotEvent) => tracker.slotOnload(slot)),
  }

  window.googletag = window.googletag || { cmd: [] }
  window.googletag.cmd.push(
    guarded(() => {
      const pubads = window.googletag?.pubads?.()
      if (!pubads || tracker.stopped) return
      Object.entries(listeners).forEach(([name, listener]) =>
        pubads.addEventListener(name, listener)
      )
      cleanups.push(() =>
        Object.entries(listeners).forEach(([name, listener]) =>
          pubads.removeEventListener(name, listener)
        )
      )
    })
  )
}

export default defineNuxtPlugin({
  name: 'ad-request-lifecycle',
  setup() {
    const config = useRuntimeConfig()
    if (
      !config.public.datadogRumApplicationId ||
      !config.public.datadogRumClientToken
    ) {
      return
    }
    // Resolved here, inside the plugin's Nuxt context: a prerendered page starts
    // tracking later, from an event listener, where composables can't run.
    const router = useRouter()
    // Only a measurement, so a failure here must never take the page down.
    const start = () => {
      try {
        startTracking(router)
      } catch (error) {
        setAdRequestLifecycleTracker(undefined)
        logger.warn('Could not start measuring ad requests', error)
      }
    }
    // A prerendered page starts once the visitor is shown it. A page that
    // loaded in a background tab reports nothing: its timings would be about
    // the visitor, not the page.
    if (isPrerendering()) {
      document.addEventListener('prerenderingchange', start, { once: true })
    } else if (document.visibilityState !== 'hidden') {
      start()
    }
  },
})
